import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Target, 
  FileEdit, 
  Upload, 
  TrendingUp, 
  CheckCircle2, 
  ArrowRight,
  Lightbulb,
  Play
} from 'lucide-react';
import { useUserActions } from '../../hooks/useUserActions';

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  action: string;
  completed: boolean;
}

interface DashboardWorkflowHelperProps {
  onNavigateToTab: (tab: string, subTab?: string) => void;
}

export function DashboardWorkflowHelper({ onNavigateToTab }: DashboardWorkflowHelperProps) {
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  const [progress, setProgress] = useState({
    hasProjects: false,
    hasJobDescriptions: false,
    hasAnalyses: false,
    hasViewedMarketData: false
  });

  // Use the user actions hook
  const { getWorkflowProgress, trackStepInitiation, migrateLegacyData } = useUserActions();

  // Load workflow progress from Supabase
  useEffect(() => {
    const loadProgress = async () => {
      try {
        // Migrate legacy localStorage data if needed
        await migrateLegacyData();
        
        // Get current progress
        const currentProgress = await getWorkflowProgress();
        setProgress(currentProgress);
      } catch (error) {
        console.error('Error loading workflow progress:', error);
      }
    };

    loadProgress();
  }, [getWorkflowProgress, migrateLegacyData]);

  const workflows = [
    {
      id: 'hiring-project',
      title: 'Complete Hiring Workflow',
      description: 'End-to-end hiring process from job creation to candidate analysis',
      steps: [
        {
          id: 'create-project',
          title: 'Create Hiring Project',
          description: 'Set up a new hiring project with requirements',
          icon: Target,
          action: 'projects',
          completed: progress.hasProjects
        },
        {
          id: 'generate-job',
          title: 'Generate Job Description',
          description: 'Create a professional job description with Smart technology',
          icon: FileEdit,
          action: 'job-generator',
          completed: progress.hasJobDescriptions
        },
        {
          id: 'analyze-resumes',
          title: 'Analyze Candidates',
          description: 'Upload and analyze candidate resumes',
          icon: Upload,
          action: 'analysis',
          completed: progress.hasAnalyses
        },
        {
          id: 'review-market',
          title: 'Review Market Data',
          description: 'Check salary trends and market insights',
          icon: TrendingUp,
          action: 'market-intelligence',
          completed: progress.hasViewedMarketData
        }
      ]
    }
  ];

  const calculateProgress = (steps: WorkflowStep[]) => {
    const completedSteps = steps.filter(step => step.completed).length;
    return (completedSteps / steps.length) * 100;
  };

  const handleStepAction = async (action: string, stepId: string) => {
    if (action === 'analysis') {
      onNavigateToTab('analysis', 'new-analysis');
    } else {
      onNavigateToTab(action);
    }
    
    // Mark step as initiated in Supabase
    try {
      await trackStepInitiation(stepId, stepId, 'hiring-project');
    } catch (error) {
      console.error('Error tracking step initiation:', error);
    }
  };

  if (selectedWorkflow) {
    const workflow = workflows.find(w => w.id === selectedWorkflow);
    if (!workflow) return null;

    const progressPercentage = calculateProgress(workflow.steps);

    return (
      <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
        <CardHeader className="p-8 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-slate-900 rounded-xl flex items-center justify-center">
                <Play className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-slate-900">{workflow.title}</CardTitle>
                <CardDescription className="text-slate-600 mt-1">{workflow.description}</CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedWorkflow(null)}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
            >
              Close
            </Button>
          </div>
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-900">Progress</span>
              <span className="text-sm text-slate-600">
                {workflow.steps.filter(s => s.completed).length} of {workflow.steps.length} completed
              </span>
            </div>
            <div className="relative">
              <Progress value={progressPercentage} className="h-2 bg-slate-100" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-8 pt-2">
          <div className="space-y-4">
            {workflow.steps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`group flex items-center gap-5 p-5 rounded-xl border transition-all duration-200 ${
                    step.completed
                      ? 'bg-green-50 border-green-200'
                      : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:bg-slate-100'
                  }`}
                >
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    step.completed
                      ? 'bg-green-500 shadow-sm'
                      : 'bg-slate-200 group-hover:bg-slate-300'
                  }`}>
                    {step.completed ? (
                      <CheckCircle2 className="h-6 w-6 text-white" />
                    ) : (
                      <Icon className="h-6 w-6 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 text-base">{step.title}</h4>
                    <p className="text-sm text-slate-600 mt-1">{step.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {step.completed ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200 font-medium px-3 py-1.5 rounded-lg">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Complete
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleStepAction(step.action, step.id)}
                        className="bg-slate-900 hover:bg-slate-800 text-white font-medium px-4 py-2 rounded-lg"
                      >
                        Start
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6">
      {workflows.map((workflow) => {
        const progressPercentage = calculateProgress(workflow.steps);
        const completedSteps = workflow.steps.filter(s => s.completed).length;
        
        return (
          <Card key={workflow.id} className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedWorkflow(workflow.id)}>
            <CardHeader className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-slate-900 rounded-xl flex items-center justify-center">
                  <Lightbulb className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg font-bold text-slate-900">{workflow.title}</CardTitle>
                  <CardDescription className="text-slate-600 mt-1">{workflow.description}</CardDescription>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900">{completedSteps}/{workflow.steps.length}</div>
                  <div className="text-xs text-slate-600">Steps complete</div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Overall Progress</span>
                  <span className="text-sm text-slate-600">{Math.round(progressPercentage)}%</span>
                </div>
                <Progress value={progressPercentage} className="h-2 bg-slate-100" />
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {workflow.steps.map((step) => (
                    <div
                      key={step.id}
                      className={`w-2 h-2 rounded-full ${
                        step.completed ? 'bg-green-500' : 'bg-slate-300'
                      }`}
                    />
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  View Details
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
} 